// IE 2026 10 02 / ODOO-5800 (заказ Игоря: копирование в Excel for Mac с заливкой — HTML-исходник в text/plain)
// «Copy for Excel»: the selection of a grid as the HTML SOURCE of a table that goes to the clipboard as text/plain ONLY.
// Measured by Igor in Excel for Mac on 02.10.2026 (copytest2.html, variant N4): of the forms tried only this one is pasted WITH the cell
// fills; any text/html flavour next to it makes Excel for Mac take its lossy HTML path (no fill). The proven form is one line, no
// <html> / <meta> / <style> wrapper:
//   <table border="1" cellspacing="0" cellpadding="4" style="border-collapse:collapse"><tbody><tr>
//   <td bgcolor="#RRGGBB" style="background-color:#RRGGBB;color:#RRGGBB;font-weight:bold;text-align:right"><b><font color="#RRGGBB">text</font></b></td>…
// The module is pure (no imports, no DOM): the writer, the reader for our own paste (a WebSCM grid must receive the same values as from
// the TSV), the TSV helper and the per-browser state (toggle «Ctrl+C copies for Excel», memo of the last copy).
// Unit test: tools/probes/p237_unit.mjs in the lane folder of ie-webscm7 (round trip TSV → Excel form → TSV).

export const COPY_EXCEL_TOGGLE_KEY = 'webscm.copyExcel'       // '1' — Ctrl+C copies for Excel, '0' — as usual, absent — never chosen (fe.ux.copyExcelDefault)
export const COPY_EXCEL_MEMO_KEY = 'webscm.copyExcel.memo'    // JSON { h: <the clipboard string>, t: <its TSV> } of the LAST Excel-form copy
export const COPY_EXCEL_MEMO_MAX = 1000000                     // characters (string + TSV): a larger copy is not kept in localStorage

const TABLE_PROVEN = '<table border="1" cellspacing="0" cellpadding="4" style="border-collapse:collapse">'   // byte for byte the tag of the proven sample
const TABLE_PLAIN = '<table cellspacing="0" cellpadding="4" style="border-collapse:collapse">'               // the same without border="1" (the grid has no such borders)

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

// counters for probes: window._copyExcel
export function copyExcel_count(key) {
    try {
        if (typeof window === 'undefined') return
        const c = window._copyExcel || (window._copyExcel = { copies: 0, memoSaved: 0, memoSkipped: 0, memoHits: 0, parsed: 0, errors: 0 })
        c[key] = (c[key] || 0) + 1
    } catch (e) { /* counters only */ }
}

// text of a cell → HTML: escaped, line breaks → <br>, TAB → &#9; (the clipboard string stays ONE line and holds no TAB — nothing in it
// can be taken for TSV); ascii — every non-ASCII character as a numeric reference (fe.ux.copyExcelAscii)
function cellHtml(text, ascii) {
    let h = esc(text).replace(/\r\n|\r|\n/g, '<br>').replace(/\t/g, '&#9;')
    if (ascii) h = h.replace(/[\u0080-\u{10ffff}]/gu, (ch) => '&#' + ch.codePointAt(0) + ';')
    return h
}

// The writer. cells: { <row>: { <col>: { text, num, xfmt, datefmt, style: { bg, color, bold } | null } } } — the model copying() builds for
// text/html (AttrGrid.copy_cells); sortedRows / sortedCols — its keys in order. A cell carries only what it has: no fill — no bgcolor /
// background-color, default text colour — no color / <font>, not bold — no font-weight / <b>. Numbers: the cell text is the machine
// number, right-aligned, plus Excel's own x:num="<raw>" and mso-number-format (LAST in the style) so that Excel shows the format of
// the grid; texts carry mso-number-format:'\@' and dates their format — as the text/html dialects do. Italic / underline are not carried.
// opt: borders — the proven <table border="1" …> (fe.ux.copyExcelBorders); formats: false — no x:num / mso-number-format at all
// (fe.ux.copyExcelFormats); ascii (fe.ux.copyExcelAscii)
export function copyExcel_table(cells, sortedRows, sortedCols, opt) {
    const o = opt || {}
    const formats = o.formats !== false, ascii = o.ascii === true
    let out = (o.borders === true ? TABLE_PROVEN : TABLE_PLAIN) + '<tbody>'
    for (const r of sortedRows) {
        out += '<tr>'
        for (const c of sortedCols) {
            const cell = cells && cells[r] ? cells[r][c] : null
            if (!cell) { out += '<td></td>'; continue }
            const S = cell.style || {}
            const text = String(cell.text ?? '')
            const isNum = cell.num !== null && cell.num !== undefined && !!cell.xfmt
            const st = []
            let at = '', inner = cellHtml(text, ascii)
            if (S.bg) { at += ` bgcolor="${esc(S.bg)}"`; st.push('background-color:' + S.bg) }
            if (S.color) { st.push('color:' + S.color); if (inner !== '') inner = `<font color="${esc(S.color)}">${inner}</font>` }
            if (S.bold === true) { st.push('font-weight:bold'); if (inner !== '') inner = `<b>${inner}</b>` }
            if (isNum) st.push('text-align:right')
            if (formats) {   // Excel's own parts LAST: an importer that stops at them has already read the fill and the colour
                if (isNum) st.push(`mso-number-format:'${cell.xfmt}'`)
                else if (cell.datefmt) st.push(`mso-number-format:'${cell.datefmt}'`)
                else if (text !== '') st.push("mso-number-format:'\\@'")
            }
            if (st.length) at += ` style="${esc(st.join(';'))}"`
            if (isNum && formats) at += ` x:num="${esc(cell.num)}"`
            out += `<td${at}>${inner}</td>`
        }
        out += '</tr>'
    }
    return out + '</tbody></table>'
}

const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' }
function decode(s) {
    if (s.indexOf('&') < 0) return s
    return s.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi, (m, e) => {
        if (e[0] === '#') {
            const n = (e[1] === 'x' || e[1] === 'X') ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10)
            try { return String.fromCodePoint(n) } catch (err) { return m }
        }
        const v = ENTITIES[e.toLowerCase()]
        return v === undefined ? m : v
    })
}

const TABLE_PARTS = { tbody: 1, thead: 1, tfoot: 1, colgroup: 1, col: 1 }
const NUM_RE = /^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i

// The reader. Text of a bare table → matrix of strings: x:num for the numbers, the text of the cell for the rest (<br> → line break,
// other tags dropped, entities decoded). null — the text is not a bare table (text or foreign tags outside the cells, nested table,
// nothing after the last cell row…): the caller then leaves the clipboard text as it is
export function copyExcel_parse(text) {
    if (typeof text !== 'string') return null
    const s = text.trim()
    if (!/^<table[\s>]/i.test(s) || !/<\/table>$/i.test(s)) return null
    const TAG = /<(\/?)([a-z][a-z0-9:]*)((?:"[^"]*"|'[^']*'|[^'">])*)>/gi
    const rows = []
    let row = null, cell = null, open = false, closed = false, pos = 0, m
    const endCell = () => { if (cell) { row.push(cell.num !== null ? cell.num : cell.text); cell = null } }
    const endRow = () => { endCell(); if (row) { rows.push(row); row = null } }
    while ((m = TAG.exec(s))) {
        const between = s.slice(pos, m.index)
        pos = TAG.lastIndex
        if (closed) return null   // something after </table>
        if (between !== '') {
            if (cell) cell.text += decode(between)
            else if (between.trim() !== '') return null   // text outside the cells
        }
        const close = m[1] === '/', name = m[2].toLowerCase()
        if (name === 'table') {
            if (close) { if (!open) return null; endRow(); open = false; closed = true }
            else { if (open) return null; open = true }   // a nested table is not our form
        } else if (!open) return null
        else if (name === 'tr') { endRow(); if (!close) row = [] }
        else if (name === 'td' || name === 'th') {
            if (close) { endCell(); continue }
            if (!row) row = []
            endCell()
            const n = /(?:^|\s)x:num\s*=\s*(?:"([^"]*)"|'([^']*)')/i.exec(m[3])
            const num = n ? decode(n[1] !== undefined ? n[1] : n[2]).trim() : ''
            cell = { text: '', num: NUM_RE.test(num) ? num : null }
        } else if (name === 'br') { if (cell) cell.text += '\n' }
        else if (!cell && !TABLE_PARTS[name]) return null   // a tag between the cells that is no part of a table
    }
    if (!closed || pos !== s.length) return null
    let width = 0
    for (const r of rows) if (r.length > width) width = r.length
    if (!rows.length || !width) return null
    for (const r of rows) while (r.length < width) r.push('')
    return rows
}

// matrix → TSV with the quoting the grid copy uses for its text/plain (a cell with TAB / line break / quote is quoted, quotes doubled)
export function copyExcel_tsv(matrix) {
    const q = (v) => { const x = String(v ?? ''); return /[\t\n\r"]/.test(x) ? '"' + x.replace(/"/g, '""') + '"' : x }
    return matrix.map(r => r.map(q).join('\t')).join('\n')
}

// ---- memo of the LAST Excel-form copy: the exact clipboard string → the TSV the same copy would have put into text/plain. The page
// keeps it in memory (any size), localStorage keeps it for the other tabs of the browser (up to COPY_EXCEL_MEMO_MAX characters)
let _memo = null     // { h, t }
let _parsed = null   // { h, t } — the last parsed text (a multi-range paste hands the same text once per range)
export function copyExcel_memoSet(html, tsv) {
    _memo = { h: String(html), t: String(tsv) }
    copyExcel_count('copies')
    try {
        if (_memo.h.length + _memo.t.length > COPY_EXCEL_MEMO_MAX) { localStorage.removeItem(COPY_EXCEL_MEMO_KEY); copyExcel_count('memoSkipped'); return false }
        localStorage.setItem(COPY_EXCEL_MEMO_KEY, JSON.stringify(_memo))
        copyExcel_count('memoSaved')
        return true
    } catch (e) {   // private mode / quota: the stale memo of an older copy must not stay
        try { localStorage.removeItem(COPY_EXCEL_MEMO_KEY) } catch (e2) { /* no storage at all */ }
        return false
    }
}
export function copyExcel_memoGet(html) {
    if (_memo && _memo.h === html) return _memo.t
    try {
        const raw = localStorage.getItem(COPY_EXCEL_MEMO_KEY)
        if (!raw) return null
        const m = JSON.parse(raw)
        if (m && typeof m.h === 'string' && typeof m.t === 'string' && m.h === html) return m.t
    } catch (e) { /* private mode / foreign format */ }
    return null
}

// clipboard text → the text for the paste of a grid: our Excel form → its TSV (memo of the last copy, otherwise the parsed table);
// anything else — as it is
export function copyExcel_fromClip(text) {
    if (typeof text !== 'string' || !/^\s*<table[\s>]/i.test(text)) return text
    let tsv = copyExcel_memoGet(text)
    if (tsv === null) { const s = text.trim(); if (s !== text) tsv = copyExcel_memoGet(s) }
    if (tsv !== null) { copyExcel_count('memoHits'); return tsv }
    if (_parsed && _parsed.h === text) return _parsed.t
    const matrix = copyExcel_parse(text)
    if (!matrix) return text
    tsv = copyExcel_tsv(matrix)
    _parsed = { h: text, t: tsv }
    copyExcel_count('parsed')
    return tsv
}

// ---- toggle of the browser «Ctrl+C copies for Excel»: localStorage COPY_EXCEL_TOGGLE_KEY; def — the state of a browser that never chose
let _toggleMem = null   // the choice of this page when localStorage refused the write
export function copyExcel_toggleGet(def) {
    if (_toggleMem !== null) return _toggleMem
    try {
        const v = localStorage.getItem(COPY_EXCEL_TOGGLE_KEY)
        if (v === '1') return true
        if (v === '0') return false
    } catch (e) { /* private mode */ }
    return def === true
}
export function copyExcel_toggleSet(on) {
    try { localStorage.setItem(COPY_EXCEL_TOGGLE_KEY, on ? '1' : '0'); _toggleMem = null; return true } catch (e) { _toggleMem = !!on; return false }
}
